import React, { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaPaperPlane, FaSpinner } from 'react-icons/fa';
import ConfirmationPopup from '../../pages/Forms/Confirm';
import SendingStatusPopup from '../../pages/Forms/SendingStatusPopup';
import API_ENDPOINTS from '../../api';

const ContactForm = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  // State Management
  const [formData, setFormData] = useState({
    type: t('contact.form.inquiry'),
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [status, setStatus] = useState('');

  // Handle Input Changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
  };

  // Validate form
  const validateForm = () => {
    if (!formData.name.trim()) {
      setError(t('contact.form.error'));
      return false;
    }
    if (!formData.email.trim() || !/\S+@\S+\.\S+/.test(formData.email)) {
      setError(t('contact.form.error'));
      return false;
    }
    if (!formData.phone.trim()) {
      setError(t('contact.form.error'));
      return false;
    }
    if (!formData.message.trim()) {
      setError(t('contact.form.error'));
      return false;
    }
    return true;
  };

  // Form Submission
  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    setStatus('sending');

    const submissionData = {
      ...formData,
      lang: i18n.language,
    };

    try {
      await axios.post(API_ENDPOINTS.MAILS.CONTACT, submissionData, {
        headers: { 'Content-Type': 'application/json' },
      });

      // Reset form
      setFormData({
        type: t('contact.form.inquiry'),
        name: '',
        email: '',
        phone: '',
        message: ''
      });
      
      setStatus('success');
    } catch (err) {
      console.error('Error submitting form', err);
      setStatus('failure');
      setError(t('contact.form.error'));
    } finally {
      setLoading(false);
    }
  };

  // Confirmation Handlers
  const confirmSubmit = () => {
    if (validateForm()) {
      setShowConfirmation(true);
    }
  };

  const handleConfirm = () => {
    setShowConfirmation(false);
    handleSubmit();
  };

  const handleCancel = () => setShowConfirmation(false);
  const handleCloseStatusPopup = () => setStatus('');

  return (
    <>
      <form 
        className="space-y-6" 
        dir={isRTL ? 'rtl' : 'ltr'}
        onSubmit={(e) => e.preventDefault()}
      >
        {error && (
          <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-red-600">
            {error}
          </div>
        )}

        {/* Inquiry Type Toggle */}
        <div className="flex space-x-2">
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, type: t('contact.form.inquiry') }))}
            className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              formData.type === t('contact.form.inquiry')
                ? 'bg-secondColor text-white shadow-md transform -translate-y-1'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('contact.form.inquiry')}
          </button>
          <button
            type="button"
            onClick={() => setFormData(prev => ({ ...prev, type: t('contact.form.suggestion') }))}
            className={`flex-1 px-6 py-3 rounded-xl font-semibold transition-all duration-300 ${
              formData.type === t('contact.form.suggestion')
                ? 'bg-secondColor text-white shadow-md transform -translate-y-1'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t('contact.form.suggestion')}
          </button>
        </div>

        {/* Form Fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('contact.form.name')} *
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-secondColor focus:ring-2 focus:ring-secondColor/20 outline-none transition"
            placeholder={t('contact.form.name')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('contact.form.email')} *
          </label>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-secondColor focus:ring-2 focus:ring-secondColor/20 outline-none transition"
            placeholder={t('contact.form.email')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {t('contact.form.phone')} *
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone}
            onChange={handleChange}
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-secondColor focus:ring-2 focus:ring-secondColor/20 outline-none transition"
            placeholder={t('contact.form.phone')}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {formData.type === t('contact.form.inquiry') 
              ? t('contact.form.inquiry') 
              : t('contact.form.suggestion')} *
          </label>
          <textarea
            name="message"
            value={formData.message}
            onChange={handleChange}
            rows="5"
            className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:border-secondColor focus:ring-2 focus:ring-secondColor/20 outline-none transition resize-none"
            placeholder={t('contact.form.message')}
          />
        </div>

        <button
          type="button"
          onClick={confirmSubmit}
          disabled={loading}
          className="w-full py-4 px-6 bg-secondColor hover:bg-primeColor-600 text-white rounded-xl font-semibold flex items-center justify-center gap-3 transition-all duration-300 transform hover:-translate-y-1 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin" />
              {t('contact.form.loading')}
            </>
          ) : (
            <>
              {t('contact.form.submit')}
              <FaPaperPlane />
            </>
          )}
        </button>
      </form>

      {/* Popups */}
      {showConfirmation && (
        <ConfirmationPopup 
          onConfirm={handleConfirm} 
          onCancel={handleCancel} 
        />
      )}

      {status && (
        <SendingStatusPopup 
          status={status} 
          onClose={handleCloseStatusPopup} 
        />
      )}
    </>
  );
};

export default ContactForm;